import { useState, memo } from 'react';
import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import Box from '@mui/material/Box';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import Typography from '@mui/material/Typography';
import IconButton from '@mui/material/IconButton';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import Chip from '@mui/material/Chip';
import Divider from '@mui/material/Divider';
import { alpha, keyframes } from '@mui/material/styles';
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import EditIcon from '@mui/icons-material/Edit';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import DeleteIcon from '@mui/icons-material/Delete';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import TextFieldsIcon from '@mui/icons-material/TextFields';
import RadioButtonCheckedIcon from '@mui/icons-material/RadioButtonChecked';
import CheckBoxIcon from '@mui/icons-material/CheckBox';
import FormatListNumberedIcon from '@mui/icons-material/FormatListNumbered';
import LinearScaleIcon from '@mui/icons-material/LinearScale';
import ToggleOnIcon from '@mui/icons-material/ToggleOn';
import { useBuilderTranslation } from '@/i18n/hooks/useTypedTranslation';
import type { Question } from '@/types';

// Highlight animation for newly added questions
const highlightPulse = keyframes`
  0% { box-shadow: 0 0 0 0 rgba(20, 184, 166, 0.5); }
  70% { box-shadow: 0 0 0 10px rgba(20, 184, 166, 0); }
  100% { box-shadow: 0 0 0 0 rgba(20, 184, 166, 0); }
`;

// Question type configuration with colors and icons
const questionTypeConfig: Record<
  string,
  { labelKey: string; color: string; bgcolor: string; icon: React.ElementType }
> = {
  OPEN_TEXT: {
    labelKey: 'OPEN_TEXT',
    color: '#0891B2',
    bgcolor: alpha('#0891B2', 0.1),
    icon: TextFieldsIcon,
  },
  SINGLE_CHOICE: {
    labelKey: 'SINGLE_CHOICE',
    color: '#0ea5e9',
    bgcolor: alpha('#0ea5e9', 0.1),
    icon: RadioButtonCheckedIcon,
  },
  MULTIPLE_CHOICE: {
    labelKey: 'MULTIPLE_CHOICE',
    color: '#10b981',
    bgcolor: alpha('#10b981', 0.1),
    icon: CheckBoxIcon,
  },
  RANKED_CHOICE: {
    labelKey: 'RANKED_CHOICE',
    color: '#0F766E',
    bgcolor: alpha('#0F766E', 0.1),
    icon: FormatListNumberedIcon,
  },
  SCALE5: {
    labelKey: 'SCALE_5',
    color: '#f59e0b',
    bgcolor: alpha('#f59e0b', 0.1),
    icon: LinearScaleIcon,
  },
  TRUE_FALSE: {
    labelKey: 'TRUE_FALSE',
    color: '#ec4899',
    bgcolor: alpha('#ec4899', 0.1),
    icon: ToggleOnIcon,
  },
};

const getTypeConfig = (code?: string) => {
  return (
    questionTypeConfig[code || ''] || {
      labelKey: 'UNKNOWN',
      color: '#6b7280',
      bgcolor: alpha('#6b7280', 0.1),
      icon: TextFieldsIcon,
    }
  );
};

interface QuestionCardProps {
  question: Question;
  questionNumber: string;
  onEdit: (question: Question) => void;
  onDuplicate: (question: Question) => void;
  onDelete: (questionId: number) => void;
}

export const QuestionCard = memo(function QuestionCard({
  question,
  questionNumber,
  onEdit,
  onDuplicate,
  onDelete,
}: QuestionCardProps) {
  const { t } = useBuilderTranslation();
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);

  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: question.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  // Support both nested questionType.code and direct questionTypeCode
  const typeConfig = getTypeConfig(question.questionTypeCode || question.questionType?.code);
  const TypeIcon = typeConfig.icon;

  const handleMenuOpen = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleEdit = () => {
    handleMenuClose();
    onEdit(question);
  };

  const handleDuplicate = () => {
    handleMenuClose();
    onDuplicate(question);
  };

  const handleDelete = () => {
    handleMenuClose();
    onDelete(question.id);
  };

  return (
    <Card
      ref={setNodeRef}
      style={style}
      data-question-id={question.id}
      sx={{
        position: 'relative',
        overflow: 'hidden',
        transition: 'all 0.2s ease',
        '&:hover': {
          boxShadow: 3,
          borderColor: 'primary.light',
        },
        ...(isDragging && {
          boxShadow: 6,
          border: 2,
          borderColor: 'primary.main',
        }),
        // Highlight animation for newly added questions
        '&.highlight-new': {
          animation: `${highlightPulse} 0.6s ease-out 3`,
          border: 2,
          borderColor: 'primary.main',
        },
      }}
    >
      {/* Type color indicator bar */}
      <Box
        sx={{
          position: 'absolute',
          left: 0,
          top: 0,
          bottom: 0,
          width: 4,
          bgcolor: typeConfig.color,
        }}
      />

      <CardContent sx={{ p: 2, pl: 2.5, '&:last-child': { pb: 2 } }}>
        <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5 }}>
          {/* Drag Handle */}
          <IconButton
            {...attributes}
            {...listeners}
            size="small"
            sx={{
              cursor: 'grab',
              color: 'text.secondary',
              opacity: 0.5,
              '&:hover': { opacity: 1 },
              '&:active': { cursor: 'grabbing' },
            }}
          >
            <DragIndicatorIcon fontSize="small" />
          </IconButton>

          {/* Question Number Badge */}
          <Box
            sx={{
              minWidth: 32,
              height: 32,
              px: 1,
              borderRadius: 2,
              bgcolor: alpha('#14B8A6', 0.1),
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flexShrink: 0,
            }}
          >
            <Typography
              variant="body2"
              sx={{ fontWeight: 700, color: 'primary.main', fontSize: '0.8rem' }}
            >
              {questionNumber}
            </Typography>
          </Box>

          {/* Question Content */}
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1, flexWrap: 'wrap' }}>
              <Chip
                size="small"
                icon={<TypeIcon sx={{ fontSize: 14 }} />}
                label={t(`questionTypes.${typeConfig.labelKey}.name`)}
                sx={{
                  bgcolor: typeConfig.bgcolor,
                  color: typeConfig.color,
                  fontWeight: 500,
                  fontSize: '0.75rem',
                  '& .MuiChip-icon': { color: typeConfig.color },
                }}
              />
              {question.isRequired && (
                <Chip
                  size="small"
                  icon={<ErrorOutlineIcon sx={{ fontSize: 14 }} />}
                  label={t('question.required')}
                  color="error"
                  variant="outlined"
                  sx={{ fontSize: '0.75rem' }}
                />
              )}
              {question.options && question.options.length > 0 && (
                <Typography variant="caption" color="text.secondary">
                  {t('question.optionsCount', { count: question.options.length })}
                </Typography>
              )}
            </Box>

            <Typography
              variant="body2"
              sx={{
                fontWeight: 500,
                mb: 1,
                display: '-webkit-box',
                WebkitLineClamp: 2,
                WebkitBoxOrient: 'vertical',
                overflow: 'hidden',
              }}
            >
              {question.text}
            </Typography>

            {/* Options Preview */}
            {question.options && question.options.length > 0 && (
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                {question.options.slice(0, 4).map((opt) => (
                  <Box
                    key={opt.id}
                    sx={{ display: 'flex', alignItems: 'center', gap: 1 }}
                  >
                    <Box
                      sx={{
                        width: 14,
                        height: 14,
                        borderRadius: '50%',
                        border: 1,
                        borderColor: 'divider',
                        flexShrink: 0,
                      }}
                    />
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      noWrap
                    >
                      {opt.label}
                    </Typography>
                  </Box>
                ))}
                {question.options.length > 4 && (
                  <Typography variant="caption" color="text.secondary">
                    {t('question.moreOptions', { count: question.options.length - 4 })}
                  </Typography>
                )}
              </Box>
            )}

            {question.helpText && (
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ mt: 1, display: 'block', fontStyle: 'italic' }}
              >
                {t('question.helpPrefix')}: {question.helpText}
              </Typography>
            )}
          </Box>

          {/* Actions Menu */}
          <IconButton size="small" onClick={handleMenuOpen}>
            <MoreVertIcon fontSize="small" />
          </IconButton>
          <Menu
            anchorEl={anchorEl}
            open={open}
            onClose={handleMenuClose}
            disableScrollLock
            anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
            transformOrigin={{ vertical: 'top', horizontal: 'right' }}
          >
            <MenuItem onClick={handleEdit}>
              <ListItemIcon>
                <EditIcon fontSize="small" />
              </ListItemIcon>
              <ListItemText>{t('question.actions.edit')}</ListItemText>
            </MenuItem>
            <MenuItem onClick={handleDuplicate}>
              <ListItemIcon>
                <ContentCopyIcon fontSize="small" />
              </ListItemIcon>
              <ListItemText>{t('question.actions.duplicate')}</ListItemText>
            </MenuItem>
            <Divider />
            <MenuItem onClick={handleDelete} sx={{ color: 'error.main' }}>
              <ListItemIcon>
                <DeleteIcon fontSize="small" color="error" />
              </ListItemIcon>
              <ListItemText>{t('question.actions.delete')}</ListItemText>
            </MenuItem>
          </Menu>
        </Box>
      </CardContent>
    </Card>
  );
});

export default QuestionCard;
